import type { AuthProvider, AuthUser, TokenPair } from "../types";

/**
 * base64url → standard base64 转换
 * 修复审计发现的 atob 问题 (SEC-4): '-' → '+', '_' → '/'
 */
function base64UrlToBase64(input: string): string {
  let base64 = input.replace(/-/g, "+").replace(/_/g, "/");
  const pad = base64.length % 4;
  if (pad === 2) {
    base64 += "==";
  } else if (pad === 3) {
    base64 += "=";
  }
  return base64;
}

function decodeBase64Url(str: string): string {
  if (typeof atob === "function") {
    return atob(base64UrlToBase64(str));
  }
  return Buffer.from(str, "base64url").toString("utf-8");
}

function parseJwtPayload(jwt: string): Record<string, unknown> {
  const parts = jwt.split(".");
  if (parts.length !== 3) {
    throw new Error("Invalid JWT format: expected 3 parts");
  }
  const decoded = decodeBase64Url(parts[1]);
  return JSON.parse(decoded);
}

/** 从 JWT 解析 exp 字段（秒 → 毫秒） */
export function decodeJwtExpiry(jwt: string): number {
  const payload = parseJwtPayload(jwt);
  const exp = payload.exp;
  if (typeof exp !== "number") {
    throw new Error("JWT missing exp claim");
  }
  return exp * 1000;
}

/** 从 accessToken 解析 user 信息 */
export function parseJwtUser(
  accessToken: string,
  loginType: AuthProvider,
): AuthUser {
  const payload = parseJwtPayload(accessToken);
  return {
    uid: String(payload.sub ?? payload.uid ?? ""),
    eoaAddress: String(payload.eoa_address ?? payload.eoaAddress ?? ""),
    email: payload.email ? String(payload.email) : undefined,
    displayName: payload.display_name
      ? String(payload.display_name)
      : payload.displayName
        ? String(payload.displayName)
        : undefined,
    photoUrl: payload.photo_url
      ? String(payload.photo_url)
      : payload.photoUrl
        ? String(payload.photoUrl)
        : undefined,
    loginType,
    providerId: payload.provider_id ? String(payload.provider_id) : undefined,
  };
}

/** 将 API 返回的 token 对转为 TokenPair */
export function parseApiTokens(apiResult: {
  access_token: string;
  refresh_token: string;
}): TokenPair {
  return {
    accessToken: apiResult.access_token,
    refreshToken: apiResult.refresh_token,
    expiresAt: decodeJwtExpiry(apiResult.access_token),
  };
}
